// client/src/utils/chatUtils.js
import { formatDistanceToNow } from 'date-fns'

/**
 * Chat utility functions and constants
 */

// Constants for chat functionality
export const CHAT_CONSTANTS = {
  AUTO_SCROLL_THRESHOLD: 100, // pixels from bottom to maintain auto-scroll
  MESSAGE_PREVIEW_LENGTH: 50, // characters for message preview
  MESSAGE_MAX_LENGTH: 1000, // maximum message length
  TEXTAREA_MAX_HEIGHT: 120, // pixels for auto-resizing textarea
  CONNECTION_TIMEOUT: 15000, // milliseconds for WebSocket connection timeout
  RECONNECT_TIMEOUT: 10000, // milliseconds for reconnection attempts
}

/**
 * Format channel name based on channel type and properties
 * @param {Object} channel - The channel object
 * @returns {string} Formatted channel name
 */
export const formatChannelName = (channel) => {
  if (!channel) return 'Chat'

  if (channel.type === 'division') {
    return (
      channel.display_name ||
      channel.division_name ||
      `Division ${channel.division_id || ''}`.trim()
    )
  }

  if (channel.type === 'league') {
    return channel.display_name || channel.league_name || 'League Chat'
  }

  if (channel.type === 'dm') {
    return channel.display_name || channel.dm_partner_name || 'Direct Message'
  }

  return 'Chat'
}

/**
 * Format message preview for channel list
 * @param {Object} channel - The channel object with latest_message
 * @returns {string} Formatted message preview
 */
export const formatChannelPreview = (channel) => {
  const message = channel.latest_message
  if (!message) {
    return 'No messages yet'
  }

  const timeAgo = formatDistanceToNow(new Date(message.created_at), {
    addSuffix: true,
  })

  const truncatedBody =
    message.body.length > CHAT_CONSTANTS.MESSAGE_PREVIEW_LENGTH
      ? `${message.body.substring(0, CHAT_CONSTANTS.MESSAGE_PREVIEW_LENGTH)}...`
      : message.body

  return `${message.firstname}: ${truncatedBody} • ${timeAgo}`
}

/**
 * Check if user should auto-scroll in message list
 * @param {HTMLElement} element - The scrollable element
 * @returns {boolean} Whether user is near bottom
 */
export const shouldAutoScroll = (element) => {
  if (!element) return true

  const { scrollTop, scrollHeight, clientHeight } = element
  return (
    scrollHeight - scrollTop <=
    clientHeight + CHAT_CONSTANTS.AUTO_SCROLL_THRESHOLD
  )
}
